/**
 * Pure open/closed decision + best-effort sessionStorage memory for Refine's
 * "Threads Meira is noticing" card — the owner's live mobile-QA finding
 * (2026-07-06 mobile pass, this lane's brief: "I hate that the thread should
 * be collapsed as well, we need as much screen real estate as we can on
 * mobile... that's how people are gonna interact with it ninety percent of
 * the time").
 *
 * REUSES THE JOBDETAIL PATTERN, NOT ITS FUNCTIONS: JobDetail's own
 * collapsibleSection.ts (R279) establishes the house idiom this module
 * mirrors exactly — a closed-by-default state, a pure toggle, and a
 * sessionStorage read/write pair that degrades to the honest default on any
 * failure — but that module's own storage key and type are JOB-scoped
 * (storageKey(jobId), and CollapsibleSectionId is a closed union of
 * job-detail-only section ids with no member for a threads card). Refine has
 * no jobId; bolting a job-shaped API onto a page with no job would be the
 * wrong reuse. This sibling module keeps the identical shape — same
 * default/toggle/read/write contract, same degrade-to-honest-default
 * discipline — generalized over a `surfaceKey` string instead, so any other
 * always-expanded informational block this page (or a future page) adds
 * later has one small, already-proven module to reach for rather than a
 * second one being invented per screen. Kept framework-free (no React, no DOM
 * at module scope) for the same reason collapsibleSection.ts is: directly
 * unit-testable without mounting a component (this repo has no jsdom/RTL —
 * see vitest.config.ts's own header comment).
 *
 * SCOPED PER SURFACE, PER TAB SESSION: sessionStorage (not localStorage),
 * matching collapsibleSection.ts's own "remembers within the session"
 * discipline — a user who expands the threads card once keeps it open for
 * the rest of this tab session, but a fresh tab (or a fresh sign-in) sees the
 * honest collapsed default again.
 */

/** Every surface this module currently backs. Refine's threads card is the
 *  first; the brief's own sweep instruction ("apply the SAME treatment ONLY
 *  where it's the same obvious class") is what would add a second member here
 *  later — never invented ahead of an actual always-expanded section. */
export type CollapsibleSurfaceId = 'refine-threads';

/** DEFAULT is collapsed for every surface here — mirrors
 *  COLLAPSIBLE_SECTION_DEFAULT's single-source-of-truth shape exactly, so a
 *  future surface added to the union above declares its own default here
 *  once, rather than each call site guessing. */
export const COLLAPSIBLE_SURFACE_DEFAULT: Record<CollapsibleSurfaceId, boolean> = {
  'refine-threads': false,
};

/** Read this surface's remembered open/closed state for THIS tab session.
 *  Returns the honest closed default on a cold read, a missing/corrupt
 *  entry, a storage-disabled context, or the non-browser (test)
 *  environment — never throws, never invents an open section the user never
 *  actually expanded. */
export function readSurfaceCollapsed(surface: CollapsibleSurfaceId): boolean {
  if (!hasSessionStorage()) return COLLAPSIBLE_SURFACE_DEFAULT[surface];
  try {
    const raw = window.sessionStorage.getItem(storageKey(surface));
    if (raw === null) return COLLAPSIBLE_SURFACE_DEFAULT[surface];
    return raw === 'open';
  } catch {
    return COLLAPSIBLE_SURFACE_DEFAULT[surface];
  }
}

/** Persist this surface's open/closed state for the rest of THIS tab
 *  session. Best-effort only (same posture as collapsibleSection.ts's
 *  writeCollapseState) — a storage failure just means the section resets to
 *  its honest closed default next time, never a thrown error surfaced to the
 *  user over a purely cosmetic preference. */
export function writeSurfaceCollapsed(surface: CollapsibleSurfaceId, open: boolean): void {
  if (!hasSessionStorage()) return;
  try {
    window.sessionStorage.setItem(storageKey(surface), open ? 'open' : 'closed');
  } catch {
    // Best-effort only — see header comment.
  }
}

/** True in any real browser and in a jsdom/happy-dom test environment; false
 *  in this repo's actual vitest run (plain Node, no DOM globals) and any
 *  other non-browser caller. Checked explicitly, same reasoning as
 *  collapsibleSection.ts's hasSessionStorage. */
function hasSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

function storageKey(surface: CollapsibleSurfaceId): string {
  return `throughline:surface-collapse:v1:${surface}`;
}

/**
 * The honest summary line for Refine's threads card when collapsed — NO
 * INVENTION: the count is always derived from the served thread array's own
 * length (resolveRealThreads' return value), never a hardcoded or guessed
 * number. `null` (no synthesis pass yet, or none found — see
 * refineThreadsSource.ts) has no count to report at all, so this returns the
 * honest fallback copy instead of a fabricated "0 threads."
 */
export function summarizeThreadsCount(realThreadsCount: number | null): string {
  if (realThreadsCount === null || realThreadsCount <= 0) return 'Tap to open';
  return realThreadsCount === 1 ? '1 thread — tap to open' : `${realThreadsCount} threads — tap to open`;
}
